import { RfqTemplateType } from '@lune-fe/dapi-models'
import {
    ButtonNew,
    LabeledFormSection,
    LoadingWrapper,
    SelectNew,
    Text,
} from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import Stack from '@mui/material/Stack'
import { AxiosError } from 'axios'
import { Worksheet } from 'exceljs'
import { FormApi } from 'final-form'
import { useSnackbar } from 'notistack'
import { FC, useCallback, useEffect, useState } from 'react'
import { Field, FieldRenderProps, Form } from 'react-final-form'
import { None, Option } from 'ts-results-es'

import { notify } from 'bugsnag'
import { FormInput } from 'components/FormInput'
import { useLLMColumnMapping } from 'hooks/useLLMColumnMapping'
import { StageContext } from 'views/CalculateEmissions/RFQ/components/MultiStepFlow'
import { csvToSheet, generateWorkingData } from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import { CsvSheet, loadUnitsArray, RowNumber } from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import { RFQState, RFQTemplateState } from 'views/CalculateEmissions/RFQ/utils/types'
import {
    detectHeaderRow,
    getTableDimensions,
    inferPropertyMapping,
    showClientAccountsStep,
} from 'views/CalculateEmissions/RFQ/utils/utils'

export enum FormKeys {
    SHEET_NAME = 'sheetName',
    HEADER_ROW = 'headerRow',
    NUMBER_OF_LEGS = 'numberOfLegs',
}

interface FileSettingsProps {
    sheetName: string
    headerRow: string
    numberOfLegs: string
}

const required = (value: string | undefined) =>
    value?.trim() ? undefined : 'This is a required field'

const FileSettings: FC<{
    sheetType: RfqTemplateType
    context: StageContext
}> = ({ context, sheetType }) => {
    const { enqueueSnackbar: snackbar } = useSnackbar()

    const [state, setState] = useState<RFQState | undefined>(context.state)
    const [availableSheets, setAvailableSheets] = useState<string[]>([])
    const [sheetName, setSheetName] = useState<string>('')
    const [headerRow, setHeaderRow] = useState<string>('')
    const [numberOfLegs, setNumberOfLegs] = useState<string>('')

    useEffect(() => {
        if (!state) {
            snackbar(`File could not be found.`)
            return
        }
        setAvailableSheets(
            state.sheetType === 'rfq' ? state.workbook.worksheets.map((e) => e.name) : [],
        )
        setSheetName(state.sheetName ?? '')
        setHeaderRow(state.headerRow?.toString() ?? '')
        setNumberOfLegs(state.numberOfLegs?.toString() ?? '')
        setState(state)
    }, [])

    const [localForm, setLocalForm] = useState<FormApi<FileSettingsProps>>()
    const [localSelectedSheetName, setLocalSelectedSheetName] = useState<string>()
    const [localSelectedSheet, setLocalSelectedSheet] = useState<CsvSheet | Worksheet | undefined>(
        undefined,
    )
    const [headerRowAutoFilled, setHeaderRowAutoFilled] = useState<boolean>(false)
    const [isProcessingLLM, setIsProcessingLLM] = useState<boolean>(false)
    const [detectedHeaderRow, setDetectedHeaderRow] = useState<Option<RowNumber>>(None)

    useEffect(() => {
        if (localSelectedSheet) {
            const headerRow = detectHeaderRow(localSelectedSheet)
            setDetectedHeaderRow(headerRow)
        }
    }, [localSelectedSheet])

    useEffect(() => {
        const computeSelectedSheet = async () => {
            if (
                (state?.sheetType === 'rfq' && localSelectedSheetName === undefined) ||
                state === undefined
            ) {
                setLocalSelectedSheet(undefined)
                return
            }

            // Load the full sheet. Reuse `csvToSheet` starting from the first row
            const sheet =
                state.sheetType === 'logistics_user_template'
                    ? ((await csvToSheet(state.workbook, RowNumber.fromOneBased(1))) as CsvSheet)
                    : state.workbook.getWorksheet(localSelectedSheetName)!
            setLocalSelectedSheet(sheet)
        }

        computeSelectedSheet()
    }, [state?.workbook, state?.sheetType, localSelectedSheetName])

    const onSubmit = async (values: FileSettingsProps) => {
        if (!state || !localSelectedSheet) return

        setIsProcessingLLM(true)

        const headerRowNumber = RowNumber.fromOneBased(Number(values.headerRow))
        const sheet = localSelectedSheet
        const { nrColumns, headerStartIndex, endRow } = getTableDimensions(sheet, headerRowNumber)

        const workState: RFQTemplateState = {
            ...state,
            ...generateWorkingData(sheet, headerRowNumber, nrColumns),
            sheetName: values.sheetName,
            headerRow: headerRowNumber.toOneBased(),
            // TODO remove this `endRow` since it's no longer used
            endRow: endRow.toZeroBased(),
            numberOfLegs: Number(values.numberOfLegs),
            sheet,
            headerStartIndex,
            shippedAt: undefined,
            shipmentId: undefined,
            load: undefined,
            loadSize: undefined,
            clientAccountName: undefined,
            clientAccountCurrency: undefined,
            currentLeg: 1,
            legs: [],
            inferredHeaderRow: detectedHeaderRow.map((rn) => rn.toOneBased()).unwrapOr(undefined),
            autoFillState: { legs: [] },
        }

        // Wait for LLM inference results and use them if successful
        const llmResults = (await useLLMColumnMapping(workState)).unwrapOrElse((error) => {
            if (error instanceof AxiosError && error.response?.status === 413) {
                snackbar(
                    'The file is too large for automatic column mapping. Please map the columns manually.',
                    { variant: 'warning' },
                )
                notify(new Error(`LLM inference failed: File too large (413) - ${error.message}`))
            } else {
                snackbar('Failed to automatically map columns. Please map them manually.', {
                    variant: 'error',
                })
                notify(new Error(`LLM inference failed: ${error.message}`))
            }
            return undefined
        })

        // Special case: inferPropertyMapping depends on llmResults but it's not in the state
        // yet, we need to give it custom state.
        const workStateWithLLM = { ...workState, llmResults }
        const loadInference = inferPropertyMapping(
            workStateWithLLM,
            'load',
            loadUnitsArray,
            undefined,
        )
        const shippedAtInference = inferPropertyMapping(
            workStateWithLLM,
            'shippedAt',
            [],
            undefined,
        )
        const shipmentIdInference = inferPropertyMapping(
            workStateWithLLM,
            'shipmentId',
            [],
            undefined,
        )

        const templateData: RFQState = {
            ...workState,
            load: loadInference,
            shippedAt: shippedAtInference,
            shipmentId: shipmentIdInference,
            llmResults,
            autoFillState: {
                // Unit is always inferred as long as column is picked
                load: { column: !!loadInference, mapping: !!loadInference },
                shippedAt: { column: !!shippedAtInference, mapping: !!shippedAtInference },
                shipmentId: { column: !!shipmentIdInference, mapping: !!shipmentIdInference },
                legs: [],
            },
        }
        context.updateState(templateData)
        setIsProcessingLLM(false)
        context.navigate(
            showClientAccountsStep(templateData) ? 'clientAccountDetails' : 'shippingDetails',
        )
    }

    const FormSelect = useCallback(
        ({ input: { onChange, value }, meta }: FieldRenderProps<string>) => {
            function handleChange(value: string): void {
                setLocalSelectedSheetName(value)
                onChange(value)
            }
            return (
                <SelectNew
                    placeholder={'Select tab to use'}
                    error={meta.touched && meta.error}
                    value={value}
                    onChange={handleChange}
                    items={availableSheets.map((sheet) => {
                        return { value: sheet, label: sheet }
                    })}
                />
            )
        },
        [availableSheets],
    )

    useEffect(() => {
        if (!localForm) {
            return
        }
        // Even when we failed to detect the header row, we'll set the field's value, which
        // should change on sheet selection change. The reason is keeping the existing value
        // could be misleading as the user wouldn't be able to see if the value is there
        // because we inferred it or because the old value was kept.
        const fieldValue = detectedHeaderRow.map((rn) => rn.toOneBased().toString()).unwrapOr('')
        localForm.change(FormKeys.HEADER_ROW, fieldValue)
        if (fieldValue) {
            setHeaderRowAutoFilled(true)
        }
    }, [detectedHeaderRow, localForm])

    return (
        <>
            <Text variant={'h5'} sx={{ textAlign: 'center' }}>
                File Settings
            </Text>
            <Form
                onSubmit={onSubmit}
                initialValues={{
                    [FormKeys.SHEET_NAME]: sheetName,
                    [FormKeys.NUMBER_OF_LEGS]: numberOfLegs,
                    [FormKeys.HEADER_ROW]: headerRow,
                }}
                render={({ form }) => {
                    useEffect(() => {
                        if (!localForm) {
                            setLocalForm(form)
                        }
                    }, [form, localForm])
                    return (
                        <LoadingWrapper
                            loading={
                                (state?.sheetType === 'rfq' && availableSheets.length === 0) ||
                                isProcessingLLM
                            }
                            labels={[
                                'Analysing your file to align with our data structure...',
                                'Checking units, formats and data consistency...',
                                'Mapping columns to Lune fields...',
                                'Preparing your data. Almost there...',
                            ]}
                            sx={{ minHeight: '200px', width: '100%' }}
                        >
                            <Stack
                                direction="column"
                                sx={{
                                    width: '100%',
                                }}
                                spacing={{
                                    xs: 6,
                                }}
                            >
                                <LabeledFormSection
                                    title={sheetType === 'rfq' ? 'Tab and header' : 'Header row'}
                                    subTile={
                                        sheetType === 'rfq'
                                            ? 'Select the tab and header row we should analyse'
                                            : 'Select the header row in your file'
                                    }
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            '> div': {
                                                width: '100%',
                                                flex: '0 1 auto',
                                                '> div': {
                                                    width: '100%',
                                                },
                                            },
                                        }}
                                    >
                                        {state?.sheetType === 'rfq' && (
                                            <Box>
                                                <Field
                                                    name={FormKeys.SHEET_NAME}
                                                    placeholder={'Select tab to use'}
                                                    validate={required}
                                                    component={FormSelect}
                                                />
                                            </Box>
                                        )}
                                        <Box>
                                            <Field
                                                name={FormKeys.HEADER_ROW}
                                                placeholder={'Header row to use'}
                                                component={FormInput}
                                                autoFill={{
                                                    value: headerRowAutoFilled,
                                                    update: setHeaderRowAutoFilled,
                                                }}
                                                restrictToNumbers={true}
                                                validate={(value) => {
                                                    if (!state || !localSelectedSheet) {
                                                        return
                                                    }
                                                    if (!value?.trim()) {
                                                        return 'This is a required field'
                                                    } else if (
                                                        !Number(value) ||
                                                        Number(value) < 1
                                                    ) {
                                                        return 'Invalid header row'
                                                    }
                                                    const { headerRowNumber, endRow } =
                                                        getTableDimensions(
                                                            localSelectedSheet,
                                                            RowNumber.fromOneBased(Number(value)),
                                                        )
                                                    if (
                                                        endRow.toOneBased() ===
                                                        headerRowNumber.nextRow().toOneBased()
                                                    ) {
                                                        return 'Invalid header row'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </LabeledFormSection>

                                <LabeledFormSection
                                    title={'Number of legs'}
                                    subTile={
                                        'Set the maximum amount of legs you’d like to calculate emissions for'
                                    }
                                >
                                    <Field
                                        name={FormKeys.NUMBER_OF_LEGS}
                                        placeholder={'Set number of legs'}
                                        component={FormInput}
                                        restrictToNumbers={true}
                                        validate={required}
                                    />
                                </LabeledFormSection>
                            </Stack>
                        </LoadingWrapper>
                    )
                }}
            />
            <ButtonNew
                onClick={() => localForm?.submit()}
                disabled={!localForm || isProcessingLLM}
                loading={isProcessingLLM}
            >
                Next
            </ButtonNew>
        </>
    )
}

export default FileSettings
